/**
 * MarketingDashboard - KPI dashboard met grafieken voor Marketing OS
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Chip, CircularProgress, Grid } from '@mui/material';
import {
  Article as ContentIcon, Publish as PublishIcon,
  Warning as OverdueIcon, TaskAlt as TaskIcon,
  Event as EventIcon, Schedule as DeadlineIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  IDEA: { label: 'Idee', color: '#6b7280' },
  CONCEPT: { label: 'Concept', color: '#8b5cf6' },
  SHOOT_PLANNING: { label: 'Draaidag plannen', color: '#d97706' },
  SHOOT_SCHEDULED: { label: 'Draaidag ingepland', color: '#ea580c' },
  IN_PRODUCTION: { label: 'In Productie', color: '#2563eb' },
  REVIEW: { label: 'Review', color: '#7c3aed' },
  APPROVED: { label: 'Goedgekeurd', color: '#059669' },
  ARCHIVED: { label: 'Gearchiveerd', color: '#9ca3af' },
};

const POST_STATUS_COLORS = {
  INTERN: '#6b7280',
  CONCEPT: '#8b5cf6',
  SCHEDULED: '#2563eb',
  PUBLISHED: '#059669',
  ARCHIVED: '#9ca3af',
};

const POST_STATUS_LABELS = {
  INTERN: 'Intern',
  CONCEPT: 'Concept',
  SCHEDULED: 'Ingepland',
  PUBLISHED: 'Geplaatst',
  ARCHIVED: 'Gearchiveerd',
};

const EVENT_TYPE_COLORS = {
  launch: '#2563eb',
  campaign: '#8b5cf6',
  event: '#f59e0b',
  holiday: '#059669',
  other: '#6b7280',
};

// ─── KPI card ───────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, color, alert }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: alert ? '#ef4444' : '#1f2937' }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function MarketingDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/marketing/dashboard/stats`);
        if (!res.ok) throw new Error('Dashboard data ophalen mislukt');
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch (err) {
        console.error('[MarketingDashboard]', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build chart data from stats
  // API returns arrays of {status, count} / {postStatus, count} / {ownerName, count}
  const statusChartData = useMemo(() => {
    if (!stats?.statusDistribution) return [];
    return (Array.isArray(stats.statusDistribution) ? stats.statusDistribution : []).map(row => ({
      name: STATUS_CONFIG[row.status]?.label || row.status,
      count: row.count,
      color: STATUS_CONFIG[row.status]?.color || '#6b7280',
    }));
  }, [stats]);

  const ownerChartData = useMemo(() => {
    if (!stats?.ownerWorkload) return [];
    return (Array.isArray(stats.ownerWorkload) ? stats.ownerWorkload : [])
      .map(row => ({ name: row.ownerName || row.ownerEmail || '?', count: row.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [stats]);

  const postPieData = useMemo(() => {
    if (!stats?.postStatusDistribution) return [];
    return (Array.isArray(stats.postStatusDistribution) ? stats.postStatusDistribution : []).map(row => ({
      name: POST_STATUS_LABELS[row.postStatus] || row.postStatus,
      value: row.count,
      color: POST_STATUS_COLORS[row.postStatus] || '#6b7280',
    }));
  }, [stats]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!stats) {
    return <Typography color="text.secondary" sx={{ py: 4 }}>Geen dashboard data beschikbaar</Typography>;
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  return (
    <Box>
      {/* KPI row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<ContentIcon sx={{ color: '#2563eb' }} />} label="Actieve content items" value={stats.totalActive ?? 0} color="#2563eb" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<PublishIcon sx={{ color: '#059669' }} />} label="Gepubliceerd deze maand" value={stats.publishedThisMonth ?? 0} color="#059669" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<OverdueIcon sx={{ color: '#ef4444' }} />} label="Overdue items" value={stats.overdue ?? 0} color="#ef4444" alert={(stats.overdue ?? 0) > 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<TaskIcon sx={{ color: '#f59e0b' }} />} label="Open taken" value={stats.openTasks ?? 0} color="#f59e0b" />
        </Grid>
      </Grid>

      {/* Status distribution bar chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Status verdeling</Typography>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={statusChartData} layout="vertical" margin={{ left: 100 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="count" name="Items" radius={[0, 4, 4, 0]}>
              {statusChartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Row 3: Owner workload + Post status pie */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Werkdruk per eigenaar</Typography>
            {ownerChartData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Geen data</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ownerChartData} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Items" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Post status</Typography>
            {postPieData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Geen data</Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ResponsiveContainer width="60%" height={220}>
                  <PieChart>
                    <Pie data={postPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                      {postPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ flex: 1 }}>
                  {postPieData.map((entry) => (
                    <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
                      <Typography variant="caption">{entry.name}: {entry.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 4: Upcoming events + deadlines */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <EventIcon sx={{ color: '#6b7280' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aankomende evenementen (30 dagen)</Typography>
            </Box>
            {(!stats.upcomingEvents || stats.upcomingEvents.length === 0) ? (
              <Typography variant="body2" color="text.secondary">Geen aankomende evenementen</Typography>
            ) : (
              stats.upcomingEvents.map((ev, i) => (
                <Box key={ev.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{ev.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{formatDate(ev.date)}</Typography>
                  {ev.type && (
                    <Chip label={ev.type} size="small"
                      sx={{ fontSize: '0.65rem', height: 20, bgcolor: `${EVENT_TYPE_COLORS[ev.type] || '#6b7280'}14`, color: EVENT_TYPE_COLORS[ev.type] || '#6b7280' }} />
                  )}
                </Box>
              ))
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <DeadlineIcon sx={{ color: '#6b7280' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aankomende deadlines (14 dagen)</Typography>
            </Box>
            {(!stats.upcomingDeadlines || stats.upcomingDeadlines.length === 0) ? (
              <Typography variant="body2" color="text.secondary">Geen aankomende deadlines</Typography>
            ) : (
              stats.upcomingDeadlines.map((dl, i) => (
                <Box key={dl.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{dl.title}</Typography>
                  <Typography variant="caption" sx={{ color: new Date(dl.deadline) < new Date() ? '#ef4444' : '#6b7280' }}>
                    {formatDate(dl.deadline)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{dl.ownerName || ''}</Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
